import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/AppLayout";
import { PrivacyNotice } from "@/components/PrivacyNotice";

function useInviteEmail() {
  const [params] = useSearchParams();
  const token = params.get("invite");
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (token) api.get(`/team/invites/${token}/info`).then((r) => setEmail(r.data.email)).catch(() => {});
  }, [token]);
  return email;
}

const HERO = "https://images.unsplash.com/photo-1728138240695-54ebc33215f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Field({ id, label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} className="h-11" {...props} />
    </div>
  );
}

function LoginForm() {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(f.email, f.password); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <Field id="email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
      <Field id="password" label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
      <div className="flex justify-end -mt-2">
        <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
      </div>
      <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="login-submit-button">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
      </Button>
    </form>
  );
}

function RegisterForm() {
  const { register } = useAuth();
  const inviteEmail = useInviteEmail();
  const [f, setF] = useState({ name: "", email: "", password: "", role: "RBT", accept_privacy: false });
  const [busy, setBusy] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  useEffect(() => { if (inviteEmail) setF((x) => ({ ...x, email: inviteEmail })); }, [inviteEmail]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_privacy) return toast.error("Please read and accept the privacy notice");
    setBusy(true);
    try { await register(f); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <Field id="r-name" label="Full name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" />
      <Field id="r-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" />
      <Field id="r-pass" label="Password (8+ characters)" type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" />
      <div className="space-y-1.5">
        <Label>I am a</Label>
        <div className="grid grid-cols-2 gap-2">
          {["RBT", "BCBA"].map((r) => (
            <button
              type="button"
              key={r}
              onClick={() => setF({ ...f, role: r })}
              data-testid={`role-toggle-${r.toLowerCase()}`}
              className={`h-11 rounded-lg border text-sm font-medium transition-colors ${f.role === r ? "border-primary bg-secondary text-primary" : "hover:bg-muted text-slate-600"}`}
            >
              {r === "RBT" ? "RBT · Technician" : "BCBA · Analyst"}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-start gap-2.5 rounded-lg border bg-secondary/40 p-3">
        <Checkbox id="privacy" checked={f.accept_privacy} onCheckedChange={(v) => setF({ ...f, accept_privacy: !!v })} className="mt-0.5" data-testid="register-privacy-checkbox" />
        <label htmlFor="privacy" className="text-sm text-slate-600 leading-snug">
          I have read and accept the{" "}
          <button type="button" className="text-primary font-medium underline underline-offset-2" onClick={() => setShowNotice(true)} data-testid="privacy-notice-link">privacy notice</button>
          , including how client health information is handled.
        </label>
      </div>
      <PrivacyNotice open={showNotice} onOpenChange={setShowNotice} />
      <p className="text-xs text-slate-500">Includes a 7-day free trial. No card needed to start.</p>
      <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="register-submit-button">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
      </Button>
    </form>
  );
}

export default function AuthPage() {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden grain">
        <img src={HERO} alt="Therapist working with a child" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B3F44]/80" />
        <div className="relative"><Brand light /></div>
        <div className="relative max-w-lg fade-up">
          <h1 className="text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">
            Less paperwork.<br />More time with your clients.
          </h1>
          <p className="mt-5 text-teal-50/90 text-base leading-relaxed">
            Log trials and behaviors, speak your session notes, and get a clean, objective SOAP note ready for review and billing in under a minute.
          </p>
          <div className="mt-8 flex flex-wrap gap-2 text-xs font-mono">
            {["97153", "97155", "DTT · NET", "Prompt levels", "Medical necessity"].map((t) => (
              <span key={t} className="px-2.5 py-1 rounded border border-white/25 bg-white/10">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-teal-100/70">Built for RBTs and BCBAs</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-10"><Brand /></div>
          <h2 className="text-2xl font-semibold tracking-tight">Welcome</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Sign in to write your session notes.</p>
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="login"><LoginForm /></TabsContent>
            <TabsContent value="register"><RegisterForm /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
